<!--
	Ported from analytics-design/src/components/MediaDashboard.tsx, then rewired to real data for
	the 9 data widgets - see the component's own comment for what changed. The Filters modal stays
	exactly as in the original design (visual-only, same as before connecting the rest of the
	screen). The Media Type/Language selects and "Manage Media List" were the same, and are now
	hidden (2026-10-06, product decision 2026-10-05: not in v1) - see the @if (false) blocks below.

	SETTE redesign (CLIPPING-598): token-by-token restyle, "Reach" -> "Visibility" in all copy.
	Media type badges now use MEDIA_COLORS (tint bg + text) instead of an arbitrary categorical
	assignment (Правило А). The mentions-delta column moved from `.startsWith('+'/'-')`
	string-sniffing to a real Tone field.
-->
<div class="space-y-4">
	@if (showSkeleton) {
	<app-analytics-skeleton [kpiCount]="6" [chartCount]="2"></app-analytics-skeleton>
	} @else if (loadError) {
	<app-analytics-error-state [error]="errorState" (retry)="fetch()"></app-analytics-error-state>
	} @else if (isEmptyPeriod) {
	<app-analytics-empty-state variant="no-mentions"></app-analytics-empty-state>
	} @else {
	<div class="space-y-4" [class.opacity-50]="isRefreshing" [class.pointer-events-none]="isRefreshing">

	<!-- 1. TOP HEADER -->
	<div class="bg-surface px-4 py-3 rounded-2xl border border-line flex flex-col xl:flex-row xl:items-center justify-between gap-3">
		<div class="flex flex-col lg:flex-row lg:items-center gap-4">
			<div>
				<div class="flex items-center gap-2">
					<h1 class="overline-label text-ink">{{ 'ANALYTICS_GLOSSARY.MEDIA' | translate }}</h1>
					<span class="bg-blue-tint text-blue font-medium px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1">
						<mat-icon class="!w-3 !h-3 !text-xs">tv</mat-icon>
						<span>{{ 'ANALYTICS_MEDIA.ACTIVE_OUTLETS_BADGE' | translate : { count: uniqueMediaOutlets } }}</span>
					</span>
				</div>
				<p class="text-ink-subtle text-xs mt-0.5">{{ 'ANALYTICS_MEDIA.SUBTITLE' | translate }}</p>
			</div>

			<div class="flex flex-wrap items-center gap-3 lg:border-l lg:border-line lg:pl-4 caption">
				<!-- Hidden 2026-10-06 (product decision 2026-10-05): not in v1 (never filtered real data anyway). -->
				@if (false) {
				<div class="flex items-center gap-1.5">
					<span class="text-ink-subtle font-medium">Media Type:</span>
					<select [(ngModel)]="selectedMediaType" class="bg-surface-gray border border-line-strong text-ink text-[11px] rounded-full px-2.5 h-7 font-medium focus-visible:outline-2 focus-visible:outline-focus">
						<option value="All">All Types</option>
						<option value="TV">Television</option>
						<option value="Web">Web Portals</option>
						<option value="Print">Print Press</option>
						<option value="Radio">Radio</option>
						<option value="Social">Social Media</option>
					</select>
				</div>
				<div class="flex items-center gap-1.5">
					<span class="text-ink-subtle font-medium">Language:</span>
					<select [(ngModel)]="selectedLanguage" class="bg-surface-gray border border-line-strong text-ink text-[11px] rounded-full px-2.5 h-7 font-medium focus-visible:outline-2 focus-visible:outline-focus">
						<option value="All">All Languages</option>
						<option value="MK">Macedonian (MK)</option>
						<option value="AL">Albanian (AL)</option>
						<option value="EN">English (EN)</option>
					</select>
				</div>
				}
				<div class="flex items-center gap-2 border-l border-line pl-3">
					<span class="text-ink-subtle font-medium hidden sm:inline">{{ 'ANALYTICS_SENTIMENT.BRAND_ROLE_LABEL' | translate }}:</span>
					<div class="flex items-center gap-1.5">
						<span class="w-2.5 h-2.5 rounded-sm bg-blue shrink-0 inline-block"></span>
						<span class="font-medium text-ink">{{ 'ANALYTICS_BRANDS.OUR_BRAND_CLIENT' | translate }}</span>
					</div>
					<div class="flex items-center gap-1.5 ml-1">
						<span class="w-2.5 h-2.5 rounded-sm bg-ink-subtle shrink-0 inline-block"></span>
						<span class="font-medium text-ink-muted">{{ 'ANALYTICS_GLOSSARY.COMPETITORS' | translate }}</span>
					</div>
					<!-- Hidden 2026-10-06 (product decision 2026-10-05): not in v1. Button + modal kept, not deleted. -->
					@if (false) {
					<button type="button" (click)="openManageModal()" class="text-blue font-medium flex items-center gap-1 hover:underline ml-2 cursor-pointer">
						<span>Manage Media List</span>
						<mat-icon class="!w-3 !h-3 !text-xs">open_in_new</mat-icon>
					</button>
					}
				</div>
			</div>
		</div>

		<div class="flex flex-wrap items-center gap-2">
			<button type="button" (click)="openFiltersModal()" class="bg-surface hover:bg-surface-gray text-ink border border-line-strong font-medium px-3 h-8 rounded-full text-xs transition-colors flex items-center gap-1.5 cursor-pointer">
				<mat-icon class="!w-3.5 !h-3.5 !text-sm text-ink-subtle">tune</mat-icon>
				<span>{{ 'ANALYTICS_MEDIA.FILTERS_BUTTON' | translate }}</span>
			</button>
		</div>
	</div>

	<!-- 2. KPI CARDS -->
	<div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
		<app-kpi-tile [label]="'ANALYTICS_MEDIA.TOTAL_MENTIONS_LABEL' | translate" infoKey="ANALYTICS_INFO.COMMON.MENTIONS" icon="description" iconColorClass="text-ink-subtle" [value]="fmtInt(totalMentions)" deltaLabel="" [sparklineValues]="sparkMentions" sparklineColor="#244896" [sampleSize]="data?.mentions?.sampleSize"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_VISIBILITY_INDEX' | translate" infoKey="ANALYTICS_INFO.COMMON.VISIBILITY" icon="visibility" iconColorClass="text-ink-subtle" [value]="avgReach" [deltaLabel]="'ANALYTICS_GLOSSARY.AVG_VISIBILITY_SUBLABEL' | translate" [sparklineValues]="sparkReach" sparklineColor="#244896" [sampleSize]="data?.avgReach?.sampleSize"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_IMPACT_SCORE' | translate" infoKey="ANALYTICS_INFO.COMMON.IMPACT" icon="bolt" iconColorClass="text-ink-subtle" [value]="avgImpact" [deltaLabel]="'ANALYTICS_GLOSSARY.AVG_IMPACT_SUBLABEL' | translate" [sparklineValues]="sparkImpact" sparklineColor="#244896" [sampleSize]="data?.avgImpact?.sampleSize"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_MEDIA.OUTLETS_3PLUS_LABEL' | translate" infoKey="ANALYTICS_INFO.MEDIA.OUTLETS_3PLUS" icon="group" iconColorClass="text-ink-subtle" [value]="outletsWith3PlusMentions.toString()" [deltaLabel]="'ANALYTICS_MEDIA.IN_SELECTED_PERIOD' | translate"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.NET_SENTIMENT' | translate" infoKey="ANALYTICS_INFO.COMMON.NET_SENTIMENT" icon="track_changes" iconColorClass="text-ink-subtle" [value]="netSentimentValue" [deltaLabel]="'ANALYTICS_TOPICS.NET_SENTIMENT_SUBLABEL' | translate" [sampleSize]="data?.netSentiment?.sampleSize"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_MEDIA.UNIQUE_OUTLETS_LABEL' | translate" infoKey="ANALYTICS_INFO.MEDIA.UNIQUE_MEDIA_OUTLETS" icon="public" iconColorClass="text-ink-subtle" [value]="uniqueMediaOutlets.toString()" [deltaLabel]="'ANALYTICS_MEDIA.COVERING_SELECTED_PERIOD' | translate"></app-kpi-tile>
	</div>

	<!-- 3. MAIN WIDGETS GRID -->
	<div class="space-y-4">
		<!-- ROW 1: Widgets 1, 2, 3 -->
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
			<!-- WIDGET 1: Top Media by Mentions -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<h3 class="section-title mb-2 flex items-center gap-1">1. {{ 'ANALYTICS_MEDIA.TOP_MEDIA_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.MEDIA.TOP_MEDIA_BY_MENTIONS"></app-info-icon></h3>
					<div class="overflow-x-auto max-h-[265px]">
						<table class="w-full text-left text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle sticky top-0 bg-surface">
									<th class="pb-2 w-6">#</th>
									<th class="pb-2">{{ 'ANALYTICS_GLOSSARY.MEDIA' | translate }}</th>
									<th class="pb-2">{{ 'ANALYTICS_BRANDS.COL_TYPE' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_TOPICS.VS_PREV' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_COVERAGE_ANALYSIS.COL_AVG_VISIBILITY' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_COVERAGE_ANALYSIS.COL_AVG_IMPACT' | translate }}</th>
								</tr>
							</thead>
							<tbody>
								@for (m of topMediaTable; track m.rank) {
								<tr class="border-t border-line hover:bg-surface-blue transition-colors">
									<td class="py-2 text-ink-subtle text-[10px] tabular-nums">{{ m.rank }}</td>
									<td class="py-2 font-medium text-ink truncate max-w-[120px]">{{ m.name }}</td>
									<td class="py-2"><span class="px-1.5 py-0.2 rounded-full text-[9px] font-medium" [class]="m.typeColorClass">{{ m.type }}</span></td>
									<td class="py-2 text-right font-medium text-ink tabular-nums">{{ fmtInt(m.mentions) }}</td>
									<td class="py-2 text-right font-medium text-[10px] tabular-nums">
										<span [class]="toneTextClass(m.changeTone)">{{ m.change }}</span>
									</td>
									<td class="py-2 text-right font-medium text-ink-muted tabular-nums">{{ m.reach }}</td>
									<td class="py-2 text-right font-medium tabular-nums" [class]="toneTextClass(m.impactTone)">{{ m.impact }}</td>
								</tr>
								}
								@if (!loading && topMediaTable.length === 0) {
								<tr><td colspan="7" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_MEDIA.NO_OUTLET_DATA' | translate }}</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
				<div class="flex items-center justify-between caption text-ink-subtle font-normal mt-2 pt-2 border-t border-line">
					<span>{{ topMediaFooter }}</span>
				</div>
			</div>

			<!-- WIDGET 2: Media Mentions Trend -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">2. {{ 'ANALYTICS_MEDIA.MEDIA_MENTIONS_TREND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.MEDIA.MEDIA_MENTIONS_TREND"></app-info-icon></h3>
					<div class="flex flex-wrap items-center gap-3 mb-1 caption font-medium">
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2.5 h-1 bg-blue rounded-full"></span> {{ 'ANALYTICS_MEDIA.TOTAL_MENTIONS_LABEL' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2.5 h-0.5 bg-chart-4"></span> {{ 'ANALYTICS_GLOSSARY.VISIBILITY' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2.5 h-1 bg-blue rounded-full"></span> {{ 'ANALYTICS_GLOSSARY.IMPACT' | translate }}</span>
					</div>
				</div>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="line" [data]="mentionsTrendChartData" [options]="mentionsTrendChartOptions" height="100%"></app-chart>
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="totalMentions"></app-sample-size-note>
				}
			</div>

			<!-- WIDGET 3: Mentions by Media Type -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div class="flex items-center justify-between mb-1">
					<h3 class="section-title flex items-center gap-1">3. {{ 'ANALYTICS_MEDIA.MENTIONS_BY_MEDIA_TYPE_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.MEDIA.MENTIONS_BY_MEDIA_TYPE"></app-info-icon></h3>
				</div>

				<div class="grid grid-cols-12 gap-2 items-center flex-1">
					<div class="col-span-7 relative w-full min-h-[250px] h-full flex items-center justify-center">
						<app-chart type="doughnut" [data]="mediaTypeChartData" [options]="mediaTypeChartOptions" height="100%"></app-chart>
						<div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center">
							<span class="text-base font-medium text-ink leading-tight tabular-nums">{{ mediaTypeTotal }}</span>
							<span class="overline-label text-ink-subtle">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</span>
						</div>
					</div>

					<div class="col-span-5 space-y-1.5 text-[10px]">
						@for (m of mediaTypeData; track m.name) {
						<div class="p-1.5 rounded-xl" [class]="m.colorClass">
							<div class="flex items-center justify-between font-medium">
								<span class="flex items-center gap-1"><span class="w-2 h-2 rounded-[3px]" [style.background-color]="m.dotColor"></span> {{ m.name }}</span>
								<span class="tabular-nums">{{ m.percentage }}</span>
							</div>
							<span class="text-[9px]">{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(m.value) } }}</span>
						</div>
						}
						@if (!loading && mediaTypeData.length === 0) {
						<div class="text-ink-subtle text-center py-2">{{ 'ANALYTICS_MEDIA.NO_MEDIA_TYPE_DATA_SHORT' | translate }}</div>
						}
					</div>
				</div>

				<div class="caption text-ink-subtle font-normal mt-2 pt-1 border-t border-line flex items-center justify-between">
					<span>{{ mediaTypeFooter }}</span>
					@if (mediaTypeTotal > 0) {
					<app-sample-size-note [count]="mediaTypeTotal"></app-sample-size-note>
					}
				</div>
			</div>
		</div>

		<!-- ROW 2: Widgets 4, 5, 6 -->
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
			<!-- WIDGET 4: Mentions by Media and Brand -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">4. {{ 'ANALYTICS_MEDIA.MENTIONS_BY_MEDIA_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.MEDIA.MENTIONS_BY_MEDIA_AND_BRAND"></app-info-icon></h3>
					<div class="flex flex-wrap items-center gap-2 mb-1 caption">
						@for (col of mediaBrandColumns; track col.key) {
						<span class="flex items-center gap-1 font-normal text-ink-muted"><span class="w-2 h-2 rounded-[3px]" [style.background-color]="col.color"></span> {{ col.label }}</span>
						}
					</div>
				</div>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="bar" [data]="mediaBrandChartData" [options]="mediaBrandChartOptions" height="100%"></app-chart>
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="totalMentions"></app-sample-size-note>
				}
			</div>

			<!-- WIDGET 5: Avg Visibility Index by Media -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<h3 class="section-title mb-1 flex items-center gap-1">5. {{ 'ANALYTICS_MEDIA.AVG_VISIBILITY_BY_MEDIA_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.MEDIA.AVG_VISIBILITY_BY_MEDIA"></app-info-icon></h3>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					@if (!loading && reachMediaChartData.labels.length === 0) {
					<div class="absolute inset-0 flex items-center justify-center text-center text-ink-subtle text-xs px-6">{{ perOutletInsufficientDataLabel }}</div>
					} @else {
					<app-chart type="bar" [data]="reachMediaChartData" [options]="reachMediaChartOptions" height="100%"></app-chart>
					}
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="totalMentions"></app-sample-size-note>
				}
			</div>

			<!-- WIDGET 6: Avg Impact Score by Media -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<h3 class="section-title mb-1 flex items-center gap-1">6. {{ 'ANALYTICS_MEDIA.AVG_IMPACT_BY_MEDIA_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.MEDIA.AVG_IMPACT_BY_MEDIA"></app-info-icon></h3>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					@if (!loading && impactMediaChartData.labels.length === 0) {
					<div class="absolute inset-0 flex items-center justify-center text-center text-ink-subtle text-xs px-6">{{ perOutletInsufficientDataLabel }}</div>
					} @else {
					<app-chart type="bar" [data]="impactMediaChartData" [options]="impactMediaChartOptions" height="100%"></app-chart>
					}
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="totalMentions"></app-sample-size-note>
				}
			</div>
		</div>

		<!-- ROW 3: Widgets 7, 8, 9 -->
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
			<!-- WIDGET 7: Media & Topic Matrix -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[370px]">
				<div>
					<div class="flex items-center justify-between mb-2">
						<h3 class="section-title flex items-center gap-1">7. {{ 'ANALYTICS_MEDIA.MEDIA_TOPIC_MATRIX_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.MEDIA.MEDIA_TOPIC_MATRIX"></app-info-icon></h3>
						<span class="caption text-ink-subtle font-normal">{{ 'ANALYTICS_MEDIA.TOP_N_TOPICS_BADGE' | translate : { count: mediaTopicColumns.length } }}</span>
					</div>
					<div class="overflow-x-auto">
						<table class="w-full text-left text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle">
									<th class="pb-2">{{ 'ANALYTICS_GLOSSARY.MEDIA' | translate }}</th>
									@for (col of mediaTopicColumns; track col) {
									<th class="pb-2 text-right truncate max-w-[70px]">{{ col }}</th>
									}
								</tr>
							</thead>
							<tbody class="text-[11px]">
								@for (row of mediaTopicMatrix; track row.media) {
								<tr class="border-t border-line hover:bg-surface-blue transition-colors">
									<td class="py-2.5 font-medium text-ink truncate max-w-[90px]">{{ row.media }}</td>
									@for (v of row.values; track $index) {
									<td class="py-2.5 text-right text-ink-muted tabular-nums">{{ v }}</td>
									}
								</tr>
								}
								@if (!loading && mediaTopicMatrix.length === 0) {
								<tr><td [attr.colspan]="mediaTopicColumns.length + 1" class="py-4 caption text-ink-subtle text-center">{{ 'ANALYTICS_MEDIA.NO_TOPIC_DATA_OUTLETS' | translate }}</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- WIDGET 8: Media Sentiment Overview -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[370px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">8. {{ 'ANALYTICS_MEDIA.MEDIA_SENTIMENT_OVERVIEW_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.MEDIA.MEDIA_SENTIMENT_OVERVIEW"></app-info-icon></h3>
					<div class="flex items-center gap-2.5 mb-2 caption font-medium">
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-positive"></span> {{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-neutral-bar"></span> {{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-negative"></span> {{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</span>
					</div>
				</div>

				<div class="space-y-2 flex-1 flex flex-col justify-center py-1">
					@for (item of mediaSentimentData; track item.outlet) {
					<div class="space-y-0.5">
						<div class="flex items-center justify-between text-[11px]">
							<span class="font-medium text-ink">{{ item.outlet }}</span>
							<span class="flex items-center gap-1 text-ink-subtle text-[10px]">
								{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(item.mentions) } }}
								@if (item.lowData) {
								<app-low-data-badge></app-low-data-badge>
								}
							</span>
						</div>
						@if (item.noData) {
						<div class="h-4 w-full bg-surface-gray rounded-full flex items-center justify-between px-2 text-[10px] text-ink-subtle">
							<span class="font-medium text-ink-muted">{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(item.mentions) } }}</span>
							<span class="text-ink-subtle italic">{{ 'ANALYTICS_SENTIMENT.TOO_FEW_MENTIONS' | translate }}</span>
						</div>
						} @else {
						<div class="h-4 w-full flex gap-[2px] text-[9px] font-medium text-on-blue" [class.opacity-50]="item.lowData">
							<div [style.width.%]="item.posPct" class="bg-positive rounded-full flex items-center justify-center">{{ item.posPct }}%</div>
							<div [style.width.%]="item.neuPct" class="bg-neutral-bar rounded-full flex items-center justify-center text-ink-subtle">{{ item.neuPct }}%</div>
							<div [style.width.%]="item.negPct" class="bg-negative rounded-full flex items-center justify-center">{{ item.negPct }}%</div>
						</div>
						}
					</div>
					}
					@if (!loading && mediaSentimentData.length === 0) {
					<div class="caption text-ink-subtle text-center py-2">{{ 'ANALYTICS_MEDIA.NO_SENTIMENT_DATA_OUTLETS' | translate }}</div>
					}
				</div>
			</div>

			<!-- WIDGET 9: Influential Stories -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[370px]">
				<div>
					<h3 class="section-title mb-2 flex items-center gap-1">9. {{ 'ANALYTICS_MEDIA.INFLUENTIAL_STORIES_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.MEDIA.INFLUENTIAL_STORIES"></app-info-icon></h3>
					<div class="overflow-x-auto max-h-[270px]">
						<table class="w-full text-left text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle sticky top-0 bg-surface">
									<th class="pb-2">{{ 'ANALYTICS_MEDIA.COL_TITLE_MEDIA' | translate }}</th>
									<th class="pb-2">{{ 'ANALYTICS_GLOSSARY.BRAND' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.VISIBILITY' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.IMPACT' | translate }}</th>
									<th class="pb-2 text-center">{{ 'ANALYTICS_MEDIA.COL_SENT_SHORT' | translate }}</th>
								</tr>
							</thead>
							<tbody class="text-[11px]">
								@for (story of topStories; track story.title) {
								<tr class="border-t border-line hover:bg-surface-blue transition-colors">
									<td class="py-2.5 font-medium text-ink truncate max-w-[150px]">
										<div class="truncate font-medium text-ink leading-tight" [title]="story.title">{{ story.title }}</div>
										<span class="text-[9px] text-ink-subtle font-normal">{{ story.media }}</span>
									</td>
									<td class="py-2.5"><span class="bg-surface-gray text-ink-muted font-medium px-1.5 py-0.2 rounded-full text-[9px]">{{ story.brand }}</span></td>
									<td class="py-2.5 text-right font-medium text-ink-muted tabular-nums">{{ story.reach }}</td>
									<td class="py-2.5 text-right font-medium tabular-nums" [class]="toneTextClass(story.impactTone)">{{ story.impact }}</td>
									<td class="py-2.5 text-center">
										<mat-icon class="!w-3.5 !h-3.5 !text-sm mx-auto" [class]="sentimentIconColorClass(story.sentiment)">{{ sentimentIcon(story.sentiment) }}</mat-icon>
									</td>
								</tr>
								}
								@if (!loading && topStories.length === 0) {
								<tr><td colspan="5" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_MEDIA.NO_HIGH_VISIBILITY_MENTIONS' | translate }}</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- 4. FOOTER NOTE -->
	<div class="caption text-ink-subtle font-normal border-t border-line pt-3 flex items-start sm:items-center gap-1.5 leading-relaxed">
		<mat-icon class="!w-3.5 !h-3.5 !text-sm text-ink-subtle shrink-0 mt-0.5 sm:mt-0">info</mat-icon>
		<span>{{ 'ANALYTICS_TOPICS.FOOTER_NOTE' | translate }}</span>
	</div>
	</div>
	}

	<!-- MODAL: Manage Media List - hidden 2026-10-06 (product decision 2026-10-05): not in v1 -->
	@if (false && showManageModal) {
	<div class="fixed inset-0 z-50 bg-ink/60 backdrop-blur-xs flex items-center justify-center p-4">
		<div class="bg-surface rounded-2xl border border-line shadow-pop max-w-lg w-full p-4 space-y-4">
			<div class="flex items-center justify-between border-b border-line pb-3">
				<div class="flex items-center gap-2">
					<mat-icon class="!w-4 !h-4 !text-base text-blue">tv</mat-icon>
					<h3 class="item-title text-ink">Outlets in the current period</h3>
				</div>
				<button type="button" (click)="closeManageModal()" class="text-ink-subtle hover:text-ink cursor-pointer">
					<mat-icon class="!w-4 !h-4 !text-base">close</mat-icon>
				</button>
			</div>

			<div class="space-y-3">
				<div class="flex items-center gap-2 bg-surface-gray border border-line-strong rounded-full px-2.5 h-9">
					<mat-icon class="!w-3.5 !h-3.5 !text-sm text-ink-subtle">search</mat-icon>
					<input type="text" [(ngModel)]="manageMediaSearch" placeholder="Search outlets by name..." class="bg-transparent text-xs text-ink placeholder-ink-subtle focus:outline-none w-full" />
				</div>

				<div class="space-y-1.5 max-h-60 overflow-y-auto pr-1">
					@for (item of topMediaTable; track item.name) {
					@if (!manageMediaSearch || item.name.toLowerCase().includes(manageMediaSearch.toLowerCase())) {
					<div class="flex items-center justify-between p-2 rounded-xl bg-surface-gray hover:bg-neutral-tint transition-colors">
						<div>
							<span class="font-medium text-ink text-xs">{{ item.name }}</span>
							<div class="flex items-center gap-2 text-[10px] text-ink-subtle"><span>{{ item.type }}</span></div>
						</div>
						<span class="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-tint text-blue">{{ fmtInt(item.mentions) }} mentions</span>
					</div>
					}
					}
				</div>
			</div>

			<div class="flex items-center justify-between border-t border-line pt-3">
				<button type="button" (click)="closeManageModal()" class="h-10 px-[18px] bg-blue hover:bg-blue-hover text-on-blue font-medium rounded-full text-xs transition-colors cursor-pointer">Done</button>
			</div>
		</div>
	</div>
	}

	<!-- MODAL: Filters (visual-only, same as the original design - see component comment) -->
	@if (showFiltersModal) {
	<div class="fixed inset-0 z-50 bg-ink/60 backdrop-blur-xs flex items-center justify-center p-4">
		<div class="bg-surface rounded-2xl border border-line shadow-pop max-w-md w-full p-4 space-y-4">
			<div class="flex items-center justify-between border-b border-line pb-3">
				<div class="flex items-center gap-2">
					<mat-icon class="!w-4 !h-4 !text-base text-blue">tune</mat-icon>
					<h3 class="item-title text-ink">{{ 'ANALYTICS_MEDIA.FILTERS_MODAL_TITLE' | translate }}</h3>
				</div>
				<button type="button" (click)="closeFiltersModal()" class="text-ink-subtle hover:text-ink cursor-pointer">
					<mat-icon class="!w-4 !h-4 !text-base">close</mat-icon>
				</button>
			</div>

			<div class="space-y-3 text-xs">
				<div>
					<label class="block text-ink-muted font-medium mb-1">{{ 'ANALYTICS_MEDIA.OUTLET_TIER_LABEL' | translate }}</label>
					<select class="w-full bg-surface-gray border border-line-strong rounded-full px-2.5 h-9 text-ink text-xs focus-visible:outline-2 focus-visible:outline-focus">
						<option>{{ 'ANALYTICS_MEDIA.OUTLET_TIER_ALL' | translate }}</option>
						<option>{{ 'ANALYTICS_MEDIA.OUTLET_TIER_TIER1' | translate }}</option>
						<option>{{ 'ANALYTICS_MEDIA.OUTLET_TIER_DIGITAL' | translate }}</option>
						<option>{{ 'ANALYTICS_MEDIA.OUTLET_TIER_PRINT' | translate }}</option>
					</select>
				</div>
				<div>
					<label class="block text-ink-muted font-medium mb-1">{{ 'ANALYTICS_MEDIA.MIN_VISIBILITY_LABEL' | translate }}</label>
					<input type="range" min="0" max="100" value="0" class="w-full accent-blue" />
					<div class="flex justify-between text-[10px] text-ink-subtle"><span>{{ 'ANALYTICS_MEDIA.PTS_0' | translate }}</span><span>{{ 'ANALYTICS_MEDIA.PTS_50' | translate }}</span><span>{{ 'ANALYTICS_MEDIA.PTS_100' | translate }}</span></div>
				</div>
				<div>
					<label class="block text-ink-muted font-medium mb-1">{{ 'ANALYTICS_MEDIA.SENTIMENT_FILTER_LABEL' | translate }}</label>
					<div class="grid grid-cols-3 gap-2">
						<button type="button" class="py-1 px-2 rounded-full bg-positive-tint text-positive font-medium text-[11px] cursor-pointer">{{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }}</button>
						<button type="button" class="py-1 px-2 rounded-full bg-neutral-tint text-ink-subtle font-medium text-[11px] cursor-pointer">{{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }}</button>
						<button type="button" class="py-1 px-2 rounded-full bg-negative-tint text-negative font-medium text-[11px] cursor-pointer">{{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</button>
					</div>
				</div>
			</div>

			<div class="flex items-center justify-end gap-2 border-t border-line pt-3">
				<button type="button" (click)="closeFiltersModal()" class="h-9 px-3 rounded-full text-ink-muted hover:bg-surface-gray text-xs font-medium cursor-pointer">{{ 'ANALYTICS_MEDIA.RESET_BUTTON' | translate }}</button>
				<button type="button" (click)="closeFiltersModal()" class="h-9 px-[18px] bg-blue hover:bg-blue-hover text-on-blue font-medium rounded-full text-xs transition-colors cursor-pointer">{{ 'ANALYTICS_MEDIA.APPLY_FILTERS_BUTTON' | translate }}</button>
			</div>
		</div>
	</div>
	}
</div>
